import { useEffect, useRef, type RefObject } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Shared modal focus behavior for the chrome primitives (ConfirmDialog,
// CommandPalette, KeyboardHelp): trap Tab inside the container, focus an
// initial element on open, restore focus to the opener on close, and close on
// Escape. Options are read through a ref so callers may pass inline closures
// without re-arming the trap every render; the trap arms/disarms on `active`
// only. Escape listens in the CAPTURE phase so a trapped dialog closes even
// when an inner widget stops propagation.
export function useFocusTrap(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
  opts: { onEscape?: () => void; initialFocus?: () => HTMLElement | null } = {},
) {
  const optsRef = useRef(opts);
  optsRef.current = opts;

  useEffect(() => {
    if (!active) return;
    const container = ref.current;
    if (!container) return;

    const opener = document.activeElement as HTMLElement | null;
    const target =
      optsRef.current.initialFocus?.() ?? container.querySelector<HTMLElement>(FOCUSABLE);
    target?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && optsRef.current.onEscape) {
        e.stopPropagation();
        optsRef.current.onEscape();
        return;
      }
      if (e.key !== "Tab") return;
      const items = [...container.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (el) => el.offsetParent !== null,
      );
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (current === first || !container.contains(current))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (current === last || !container.contains(current))) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      opener?.focus?.();
    };
  }, [ref, active]);
}
